import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

const services = [
  {
    title: 'Hydrostatic Cylinder Testing',
    subtitle: 'Certified Safety Testing',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/l3ongo0f_image.png',
    desc: 'We provide authorized and certified hydrostatic stretch testing for gas cylinders. This crucial safety procedure checks for leaks, structural flaws, and overall strength of the cylinder to ensure it can safely hold pressurized gases according to national safety standards.',
    features: ['High-pressure hydraulic test stations', 'Detailed safety evaluation and certification', 'Valve replacement and servicing', 'Compliance with national standards'],
  },
  {
    title: 'Cryogenic Vessel Maintenance',
    subtitle: 'Specialized Maintenance',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/t18ft0gh_image.png',
    desc: 'Cryogenic liquids require specialized storage. We offer comprehensive maintenance, repair, and vacuum testing for cryogenic vessels to prevent boil-off losses and ensure optimal thermal insulation performance.',
    features: ['Vacuum testing and re-evacuation', 'Safety valve and rupture disc calibration', 'On-site repair and pipeline purging', 'Thermal insulation performance audit'],
  },
  {
    title: 'Air Separation Units',
    subtitle: 'Turn-Key Projects',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/20knro6z_image_9a2a29d2.jpeg',
    desc: 'Erection, commissioning, and maintenance of Air Separation Units on a complete turn-key basis. Our plants range from 50 to 500 tons per day for liquid plants and up to 1000 tons per day for gas plants.',
    features: ['Complete turn-key delivery', 'Liquid plants: 50–500 TPD capacity', 'Gas plants: up to 1000 TPD', 'Commissioning and ongoing maintenance'],
  },
  {
    title: 'Gas Cylinder Testing & Servicing',
    subtitle: 'Periodic Inspection',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/256jmfcp_7a533340-55dd-4da9-b79b-8af66b8f095d.png',
    desc: 'Periodic testing and servicing of gas cylinders to ensure safe operation throughout their lifecycle. Our certified technicians handle all types of industrial and medical gas cylinders.',
    features: ['Periodic testing per regulations', 'Cylinder valve servicing', 'Pressure testing verification', 'Batch certification and documentation'],
  },
  {
    title: 'Gas Analysis Services',
    subtitle: 'Precision Measurement',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/czf6c6e7_a88e2e87-a052-4896-ab13-356d6db8e833.png',
    desc: 'Comprehensive evaluation of gas usage and needs to determine the most efficient and cost-effective supply mode. We analyze composition, purity, and consumption patterns to optimize your gas operations.',
    features: ['Gas composition analysis', 'Purity verification testing', 'Consumption pattern evaluation', 'Supply mode optimization'],
  },
  {
    title: 'Offshore Purging',
    subtitle: 'Specialized Operations',
    img: 'https://customer-assets.emergentagent.com/job_seva-solutions-hub/artifacts/uyy7jzbn_image_c8ba4200.jpeg',
    desc: 'Specialized offshore purging services using inert gases to safely displace hazardous or unwanted gases from pipelines, vessels, and equipment in marine and offshore environments.',
    features: ['Inert gas purging operations', 'Offshore pipeline services', 'Safety protocol compliance', 'Experienced offshore team'],
  },
];

const freightSystems = [
  { title: 'Individual Cylinders', desc: 'Single cylinder delivery for low-volume customers and specialty gas requirements.' },
  { title: 'Multiple Cylinder Packages', desc: 'Cylinder clusters and pallet systems for medium-volume industrial users.' },
  { title: 'Cryogenic Liquid Cylinders', desc: 'Vacuum-insulated liquid cylinders for efficient cryogenic liquid delivery.' },
  { title: 'Bulk Cryogenic Tankers', desc: 'Large-scale bulk delivery via mobile cryogenic tankers for high-volume customers.' },
];

export default function ServicesPage() {
  return (
    <div className="pt-20">
      {/* HERO */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-red mb-4 block">Our Services</span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tighter font-heading mb-6">
              Specialized Gas Engineering Services
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Ensuring maximum safety, compliance, and operational efficiency through dedicated gas cylinder, cryogenic vessel, and plant maintenance programs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((svc, i) => (
              <motion.div key={svc.title} {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
                data-testid={`service-${i}`}>
                <div className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                  <div className="relative overflow-hidden">
                    <img src={svc.img} alt={svc.title} className="w-full h-[360px] object-cover" />
                    <div className="absolute top-4 left-4 bg-brand-red text-white px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
                      {svc.subtitle}
                    </div>
                  </div>
                </div>
                <div className={i % 2 !== 0 ? 'lg:order-1' : ''}>
                  <span className="section-tag mb-3 block">Service {String(i + 1).padStart(2, '0')}</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight font-heading mb-4">{svc.title}</h2>
                  <p className="text-gray-600 leading-relaxed mb-6">{svc.desc}</p>
                  <ul className="space-y-3 mb-8">
                    {svc.features.map(f => (
                      <li key={f} className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-brand-red mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="btn-primary">Get a Quote</Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FREIGHT SYSTEMS */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="section-tag mb-3 block">Logistics & Delivery</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight font-heading">Our Freight Systems</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">Multiple delivery options designed to match your volume requirements and operational needs.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {freightSystems.map((fs, i) => (
              <motion.div key={fs.title} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white border border-gray-200 p-8 card-hover" data-testid={`freight-${i}`}>
                <div className="w-2 h-8 bg-brand-red mb-6" />
                <h3 className="font-bold text-gray-900 font-heading text-lg mb-3">{fs.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{fs.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-red">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl font-bold text-white font-heading mb-4">Need a Customized Solution?</h2>
            <p className="text-red-100 mb-8 max-w-xl mx-auto">Our engineering team will assess your requirements and design the optimal gas supply and maintenance solution for your operation.</p>
            <Link to="/contact" data-testid="services-cta-btn" className="bg-white text-brand-red px-10 py-4 font-semibold uppercase tracking-wider hover:bg-gray-100 transition-colors inline-block">
              Contact Our Engineers
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
